import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Breadcrumbs,
    Link as MuiLink,
    CircularProgress,
    TextField,
    MenuItem,
    Alert,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getDeliverySheet, closeDeliverySheet, markDelivered, printDeliverySheet, updateConsignmentStatus, DeliverySheet } from '../../services/deliverySheetApi';
import { ConsignmentView } from '../Consignments';
import GenericModal from '../../components/GenericModal';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';

const DeliverySheetView = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [deliverySheet, setDeliverySheet] = useState<DeliverySheet | null>(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedConsignmentId, setSelectedConsignmentId] = useState<number | null>(null);
    const { toast, showSuccess, showError, hideToast } = useToast();
    const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
    const [submitting, setSubmitting] = useState(false);

    // Scan Rejected/Reattempt state
    const [scanAction, setScanAction] = useState('');
    const [scanReason, setScanReason] = useState('');
    const [scanConsignmentNo, setScanConsignmentNo] = useState('');
    const [scanFeedback, setScanFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [scanLoading, setScanLoading] = useState(false);
    const [scannedConsignments, setScannedConsignments] = useState<Array<{ id: number; consignment_no: string; status_id: number }>>([]);
    const scanInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchDeliverySheet();
    }, [id]);

    const fetchDeliverySheet = async () => {
        try {
            setLoading(true);
            const response = await getDeliverySheet(Number(id));
            setDeliverySheet(response.data);
        } catch (error: any) {
            showError(error.message || 'Failed to load delivery sheet');
            navigate('/delivery-sheets');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setConfirmModal({
            open: true,
            title: 'Close Delivery Sheet',
            message: 'Are you sure you want to close this delivery sheet?',
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await closeDeliverySheet(Number(id));
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    showSuccess('Delivery sheet closed successfully!');
                    fetchDeliverySheet();
                } catch (error: any) {
                    showError(error.message || 'Failed to close delivery sheet');
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    const handleMarkAllDelivered = () => {
        setConfirmModal({
            open: true,
            title: 'Mark All as Delivered',
            message: 'Are you sure you want to mark all remaining consignments as delivered?',
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await markDelivered(Number(id));
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    showSuccess('All consignments marked as delivered!');
                    fetchDeliverySheet();
                } catch (error: any) {
                    showError(error.message || 'Failed to mark consignments as delivered');
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    const handleScanConsignment = async () => {
        if (!scanAction || !scanReason || !scanConsignmentNo) {
            setScanFeedback({ message: 'Please select action, reason, and enter consignment number.', type: 'error' });
            return;
        }

        if (scannedConsignments.some(c => c.consignment_no === scanConsignmentNo)) {
            setScanFeedback({ message: 'Consignment already scanned.', type: 'error' });
            return;
        }

        try {
            setScanLoading(true);
            setScanFeedback(null);
            const response = await updateConsignmentStatus(Number(id), {
                consignment_no: scanConsignmentNo,
                action: scanAction as 'Rejected' | 'Reattempted',
                reason: scanReason,
            });
            setScannedConsignments(prev => [...prev, response.data]);
            setScanFeedback({ message: response.message, type: 'success' });
            setScanConsignmentNo('');
            scanInputRef.current?.focus();
            fetchDeliverySheet();
        } catch (error: any) {
            setScanFeedback({ message: error.message || 'Failed to update consignment', type: 'error' });
        } finally {
            setScanLoading(false);
        }
    };

    const handleScanKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleScanConsignment();
        }
    };

    const handleViewConsignment = (consignmentId: number) => {
        setSelectedConsignmentId(consignmentId);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedConsignmentId(null);
    };

    const getStatusColor = (statusId: number) => {
        if (statusId === 9 || statusId === 18) return 'success'; // Delivered
        if (statusId === 10) return 'error'; // Failed
        if (statusId === 11) return 'warning'; // Reattempt
        return 'info';
    };

    const getStatusName = (statusId: number) => {
        const statusMap: Record<number, string> = {
            9: 'Delivered',
            18: 'Delivered (Paid)',
            10: 'Delivery Failed',
            11: 'Reattempt',
            8: 'Out for Delivery'
        };
        return statusMap[statusId] || 'Unknown';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!deliverySheet) {
        return null;
    }

    const canClose = deliverySheet.status_id === 22 && deliverySheet.pending_count === 0;
    const canMarkDelivered = deliverySheet.status_id !== 23 && deliverySheet.status_id !== 8 && deliverySheet.status_id !== 21;

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 3 }}>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Home
                </MuiLink>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/delivery-sheets')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Delivery Sheets
                </MuiLink>
                <Typography color="text.primary">{deliverySheet.unique_id}</Typography>
            </Breadcrumbs>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Delivery Sheet: {deliverySheet.unique_id}
                        </Typography>
                        <Box>
                            <Button
                                variant="outlined"
                                startIcon={<PrintIcon />}
                                onClick={() => printDeliverySheet(Number(id))}
                                sx={{ mr: 2 }}
                            >
                                Print
                            </Button>
                            {canMarkDelivered && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleMarkAllDelivered}
                                    sx={{ mr: 2 }}
                                >
                                    Mark Remaining as Delivered
                                </Button>
                            )}
                            {canClose && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleClose}
                                >
                                    Close Sheet
                                </Button>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 3, mb: 4, flexDirection: { xs: 'column', md: 'row' } }}>
                        {/* Basic Information */}
                        <Box sx={{ flex: 1 }}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ bgcolor: theme.palette.action.hover, p: 1, mb: 2 }}>
                                    Basic Information
                                </Typography>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Rider:</TableCell>
                                            <TableCell>{deliverySheet.rider?.name}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Created At:</TableCell>
                                            <TableCell>{new Date(deliverySheet.created_at).toLocaleString()}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Created By:</TableCell>
                                            <TableCell>{deliverySheet.user?.name}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Status:</TableCell>
                                            <TableCell>
                                                <Chip label={deliverySheet.status.name} color="primary" size="small" />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>COD Amount:</TableCell>
                                            <TableCell>{deliverySheet.total_cod_amount}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Received Amount:</TableCell>
                                            <TableCell>{deliverySheet.cod_amount_recieved}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Collected Amount:</TableCell>
                                            <TableCell>{deliverySheet.cod_amount_collected}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>

                        {/* Consignment Information */}
                        <Box sx={{ flex: 1 }}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ bgcolor: theme.palette.action.hover, p: 1, mb: 2 }}>
                                    Consignment Information
                                </Typography>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Total:</TableCell>
                                            <TableCell>{deliverySheet.total_consignments}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Delivered:</TableCell>
                                            <TableCell>{deliverySheet.delivered_count || 0}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Rejected:</TableCell>
                                            <TableCell>{deliverySheet.rejected_count || 0}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Reattempt:</TableCell>
                                            <TableCell>{deliverySheet.reattempt_count || 0}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    </Box>

                    {/* Scan Rejected/Reattempt Consignments */}
                    {(deliverySheet.pending_count ?? 0) > 0 && deliverySheet.status_id !== 21 && (
                        <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
                            <Typography variant="h6" align="center" gutterBottom>
                                Scan Rejected/Reattempt Consignments
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <TextField
                                    select
                                    label="Action"
                                    value={scanAction}
                                    onChange={(e) => setScanAction(e.target.value)}
                                    sx={{ minWidth: 200 }}
                                    size="small"
                                >
                                    <MenuItem value="">Select Action</MenuItem>
                                    <MenuItem value="Rejected">Rejected</MenuItem>
                                    <MenuItem value="Reattempted">Re-attempt</MenuItem>
                                </TextField>
                                <TextField
                                    select
                                    label="Reason"
                                    value={scanReason}
                                    onChange={(e) => setScanReason(e.target.value)}
                                    sx={{ minWidth: 300 }}
                                    size="small"
                                >
                                    <MenuItem value="">Select Reason</MenuItem>
                                    <MenuItem value="Customer not at home.">Customer not at home</MenuItem>
                                    <MenuItem value="Customer has no cash available.">Customer has no cash available</MenuItem>
                                    <MenuItem value="Customer instructs to postpone.">Customer instructs to postpone</MenuItem>
                                    <MenuItem value="Customer refuses delivery.">Customer refuses delivery</MenuItem>
                                    <MenuItem value="Customer not reachable.">Customer not reachable</MenuItem>
                                    <MenuItem value="Customer not agree to receive this package.">Customer not agree to receive this package</MenuItem>
                                    <MenuItem value="Customer address is invalid.">Customer address is invalid</MenuItem>
                                    <MenuItem value="Cod amount is wrong.">COD amount is wrong</MenuItem>
                                    <MenuItem value="Duplicate order.">Duplicate order</MenuItem>
                                    <MenuItem value="Courier has accident.">Courier has accident</MenuItem>
                                    <MenuItem value="Package damage.">Package damage</MenuItem>
                                    <MenuItem value="Customer phone number is wrong.">Customer phone number is wrong</MenuItem>
                                    <MenuItem value="Package is lost.">Package is lost</MenuItem>
                                    <MenuItem value="Road Closure/ Heavy Traffic.">Road Closure / Heavy Traffic</MenuItem>
                                    <MenuItem value="Natural Disaster.">Natural Disaster</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </TextField>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
                                <TextField
                                    inputRef={scanInputRef}
                                    label="Enter Consignment Number"
                                    value={scanConsignmentNo}
                                    onChange={(e) => setScanConsignmentNo(e.target.value)}
                                    onKeyDown={handleScanKeyDown}
                                    size="small"
                                    sx={{ minWidth: 300 }}
                                    disabled={scanLoading}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleScanConsignment}
                                    disabled={scanLoading || !scanAction || !scanReason || !scanConsignmentNo}
                                >
                                    {scanLoading ? <CircularProgress size={24} /> : 'Submit'}
                                </Button>
                            </Box>
                            {scanFeedback && (
                                <Alert severity={scanFeedback.type} sx={{ mt: 2 }}>
                                    {scanFeedback.message}
                                </Alert>
                            )}
                            {scannedConsignments.length > 0 && (
                                <TableContainer sx={{ mt: 2 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Consignment #</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {scannedConsignments.map((c) => (
                                                <TableRow key={c.id}>
                                                    <TableCell>{c.consignment_no}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={getStatusName(c.status_id)}
                                                            color={getStatusColor(c.status_id)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    )}

                    {/* Consignments Table */}
                    <Typography variant="h6" gutterBottom sx={{ bgcolor: theme.palette.action.hover, p: 1, mb: 2 }}>
                        Consignments
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Consignment #</TableCell>
                                    <TableCell>Booking Date</TableCell>
                                    <TableCell>Destination</TableCell>
                                    <TableCell>Service</TableCell>
                                    <TableCell>Order Type</TableCell>
                                    <TableCell>COD Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Reason</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {deliverySheet.consignments && deliverySheet.consignments.length > 0 ? (
                                    deliverySheet.consignments.map((consignment) => (
                                        <TableRow key={consignment.id}>
                                            <TableCell>
                                                <MuiLink
                                                    component="button"
                                                    onClick={() => handleViewConsignment(consignment.id)}
                                                    sx={{ textDecoration: 'none', cursor: 'pointer' }}
                                                >
                                                    {consignment.consignment_no}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>{new Date(consignment.created_at).toLocaleDateString()}</TableCell>
                                            <TableCell>{consignment.destinationCity?.name || '-'}</TableCell>
                                            <TableCell>{consignment.service_type}</TableCell>
                                            <TableCell>{consignment.booking_type}</TableCell>
                                            <TableCell>{consignment.cod_amount || 0}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusName(consignment.statusInDeliverySheet)}
                                                    color={getStatusColor(consignment.statusInDeliverySheet)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{consignment.reasonInDeliverySheet || '-'}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">No consignments found</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Consignment Details Modal */}
            <ConsignmentView
                open={modalOpen}
                onClose={handleCloseModal}
                consignmentId={selectedConsignmentId}
            />

            <GenericModal
                open={confirmModal.open}
                onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                title={confirmModal.title}
                maxWidth="xs"
                actions={
                    <>
                        <Button
                            onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                            disabled={submitting}
                            sx={{ color: theme.palette.text.secondary, borderRadius: 3 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={confirmModal.onConfirm}
                            disabled={submitting}
                            sx={{ borderRadius: 3 }}
                        >
                            {submitting ? 'Processing...' : 'Yes, Proceed'}
                        </Button>
                    </>
                }
            >
                <Typography variant="body1" color="text.primary">
                    {confirmModal.message}
                </Typography>
            </GenericModal>

            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default DeliverySheetView;

